import type { RequestContext } from "../../../application/context/request-context.js";
import type { LpReport } from "../../../domain/ndt/lp-report-domain.js";
import type { LpReportRepository } from "../../../repositories/operational-repositories.js";

const STORAGE_KEY = "lp_reports";

/** Espelhado em localStorage, como os demais repositórios do app real — F5 preserva o rascunho. */
export class InMemoryLpReportRepository implements LpReportRepository {
  private reports = new Map<string, LpReport>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: LpReport[] = JSON.parse(stored);
          for (const r of parsed) this.reports.set(r.id, r);
        }
      }
    } catch (e) {
      console.warn("Failed to load LP reports from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.reports.values())));
      }
    } catch (e) {
      // Fotografias em base64 podem estourar a cota do localStorage. O erro
      // não é engolido: quem salvou precisa saber que o rascunho não persistiu.
      console.error("Failed to persist LP reports to storage:", e);
      throw e;
    }
  }

  async findById(_context: RequestContext, id: string): Promise<LpReport | null> {
    this.loadFromStorage();
    return this.reports.get(id) || null;
  }

  async findByInspection(_context: RequestContext, inspectionId: string): Promise<LpReport | null> {
    this.loadFromStorage();
    for (const report of this.reports.values()) {
      if (report.inspectionId === inspectionId) return report;
    }
    return null;
  }

  async listByClient(_context: RequestContext, clientId: string): Promise<readonly LpReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values()).filter(r => r.clientId === clientId);
  }

  async listAll(): Promise<readonly LpReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values());
  }

  async save(_context: RequestContext, report: LpReport): Promise<void> {
    this.reports.set(report.id, { ...report });
    this.persistStorage();
  }
}
